import React, { useCallback, useState, useEffect, useRef } from 'react';
import { Breakpoints } from '@bitcoin-portal/bitcoincom-pkg-theme';
import { GoArrowRight } from 'react-icons/go';
import { IoIosArrowDown } from 'react-icons/io';
import Logo from './Logo';
import Hamburger from '../Hamburger';
import { handleRipple } from '../../../helpers/ripple';
import { useBreakpoint } from '../../Responsive/useBreakpoint';
import { remoteLog } from '../../../helpers/remoteLog';
import { useMPCWidget } from '../../../helpers/useMPCWidget';

import {
  Container,
  Content,
  NavbarContainer,
  NavbarWrapper,
  ItemsContainer,
  NavList,
  Item,
  NavItemWrapper,
  DropdownMenu,
  DropdownItem,
  BlankSpace,
  DesktopContainer,
  ResponsiveContainer,
  LoginButtonDesktop,
  StyledSearch,
  LoginButtonTablet,
} from './styled';

const Top = () => {
  const [isMounted, setIsMounted] = useState(false);
  const realIsDesktop = useBreakpoint(Breakpoints.xl);
  const isDesktop = isMounted ? realIsDesktop : false;
  const navbarRef = useRef(null);
  const [dropdowns, setDropdowns] = useState({ hamburger: false });
  const [openSubmenu, setOpenSubmenu] = useState(null);
  const closeTimerRef = useRef(null);
  const { hasAccount, handleLoginClick } = useMPCWidget();

  const handleSubmenuEnter = key => {
    if (closeTimerRef.current) clearTimeout(closeTimerRef.current);
    setOpenSubmenu(key);
  };

  const handleSubmenuLeave = () => {
    closeTimerRef.current = setTimeout(() => setOpenSubmenu(null), 150);
  };

  useEffect(() => {
    setIsMounted(true);
  }, []);

  const closeDrodowns = () => {
    setDropdowns(prev => ({ ...prev, hamburger: false }));
  };

  const handleButtonClick = async text => {
    await remoteLog(`User clicked on '${text}' item`);
  };

  const toggleDropdown = useCallback(name => {
    setDropdowns(prev =>
      Object.keys(prev).reduce(
        (p, c) => ({
          ...p,
          [c]: c === name ? !prev[c] : false,
        }),
        {},
      ),
    );
  }, []);

  useEffect(() => {
    const handleClickOutside = event => {
      if (navbarRef.current && !navbarRef.current.contains(event.target)) {
        closeDrodowns();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const navbarList = [
    {
      key: 'news',
      text: 'News',
      link: 'https://news.bitcoin.com/',
      openNewTab: true,
    },
    {
      key: 'wallet',
      text: 'Wallet',
      link: 'https://wallet.bitcoin.com/',
      openNewTab: true,
      submenu: [
        { key: 'bitcoin-wallet', text: 'Bitcoin Wallet', link: 'https://wallet.bitcoin.com/bitcoin/', openNewTab: false },
        { key: 'bitcoin-cash-wallet', text: 'Bitcoin Cash', link: 'https://wallet.bitcoin.com/bitcoin-cash/', openNewTab: false },
        { key: 'usdt-wallet', text: 'USDT Wallet', link: 'https://wallet.bitcoin.com/tether/', openNewTab: false },
        { key: 'usdc-wallet', text: 'USDC Wallet', link: 'https://wallet.bitcoin.com/usd-coin/', openNewTab: false },
        { key: 'polygon-wallet', text: 'Polygon Wallet', link: 'https://wallet.bitcoin.com/polygon/', openNewTab: false },
        { key: 'bnb-wallet', text: 'BNB Wallet', link: 'https://wallet.bitcoin.com/bnb/', openNewTab: false },
        { key: 'verse-wallet', text: 'Verse Wallet', link: 'https://wallet.bitcoin.com/verse/', openNewTab: false },
      ],
    },
    {
      key: 'games',
      text: 'Games',
      link: 'https://games.bitcoin.com/',
      openNewTab: true,
    },
    {
      key: 'buy',
      text: 'Buy',
      link: 'https://buy.bitcoin.com/',
      openNewTab: true,
    },
    {
      key: 'learn',
      text: 'Learn',
      link: 'https://www.bitcoin.com/get-started/',
      openNewTab: false,
    },
    {
      key: 'research',
      text: 'Research',
      link: 'https://www.bitcoin.com/research/',
      openNewTab: false,
    },
    {
      key: 'offers',
      text: 'Offers',
      link: 'https://www.bitcoin.com/directory/',
      openNewTab: false,
    },
  ];

  const loginButton = 'Login';
  const buttonText = 'Get Bitcoin';
  const buttonLink = 'https://branch.wallet.bitcoin.com/KE0EQAPrqAb';

  return (
    <Container>
      <Content ref={navbarRef}>
        <NavbarContainer>
          <NavbarWrapper>
            <Logo />
            <ItemsContainer>
              <NavList>
                {navbarList.map(item =>
                  item.submenu && item.submenu.length > 0 ? (
                    <NavItemWrapper
                      key={item.key}
                      onMouseEnter={() => handleSubmenuEnter(item.key)}
                      onMouseLeave={handleSubmenuLeave}
                    >
                      <Item
                        href={item.link}
                        target={item.openNewTab ? '_blank' : '_self'}
                        rel={item.openNewTab ? 'noopener noreferrer' : undefined}
                        onClick={() => handleButtonClick(item.text)}
                      >
                        {item.text}
                        <IoIosArrowDown
                          size={14}
                          style={{
                            transition: 'transform 0.2s',
                            transform: openSubmenu === item.key ? 'rotate(180deg)' : 'rotate(0deg)',
                          }}
                        />
                      </Item>
                      <DropdownMenu show={openSubmenu === item.key}>
                        {item.submenu.map(sub => (
                          <li key={sub.key}>
                            <DropdownItem
                              href={sub.link}
                              target={sub.openNewTab ? '_blank' : '_self'}
                            >
                              {sub.text}
                            </DropdownItem>
                          </li>
                        ))}
                      </DropdownMenu>
                    </NavItemWrapper>
                  ) : (
                    <Item
                      key={item.key}
                      href={item.link}
                      target={item.openNewTab ? '_blank' : '_self'}
                      rel={item.openNewTab ? 'noopener noreferrer' : undefined}
                      onClick={() => handleButtonClick(item.text)}
                    >
                      {item.text}
                    </Item>
                  )
                )}
                <LoginButtonDesktop
                  className="pulse"
                  onClick={handleLoginClick}
                  onMouseEnter={handleRipple}
                  style={{ display: !hasAccount ? 'flex' : 'none' }}
                >
                  {loginButton}
                  <GoArrowRight size={20} color="#1c1c1c" />
                </LoginButtonDesktop>
              </NavList>
            </ItemsContainer>
            {!isDesktop && <BlankSpace />}
            <StyledSearch locale="en" visible rebrand />
            <LoginButtonTablet
              className="pulse"
              onClick={handleLoginClick}
              onMouseEnter={handleRipple}
            >
              {loginButton}
            </LoginButtonTablet>

            <DesktopContainer>
              <div
                id="mpc-widget"
                style={{ display: hasAccount ? 'block' : 'none' }}
              />
            </DesktopContainer>
            <ResponsiveContainer>
              <Hamburger
                isOpen={dropdowns.hamburger}
                onClick={() => toggleDropdown('hamburger')}
                navbarList={navbarList}
                buttonData={{ buttonText, buttonLink }}
              />
            </ResponsiveContainer>
          </NavbarWrapper>
        </NavbarContainer>
      </Content>
    </Container>
  );
};

export default Top;
